import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import {
  func, string, bool, number
} from 'prop-types';
import RTE from 'react-rte';
import { debounce } from 'lodash-es';
import { withLang } from '../../i18n';
import styles from './richTextEditor.module.css';
import MaxLengthExceeded from '../MaxLengthExceeded';
import { getLineCount } from '../../util';

const DELAY_BEFORE_HIDE = 200;

const makeValue = (defaultValue) => (defaultValue
  ? RTE.createValueFromString(defaultValue, 'html')
  : RTE.createEmptyValue());

const getEmptyStringOrHtmlContent = (inputValue) =>
  (inputValue.getEditorState()
    .getCurrentContent()
    .hasText()
    ? inputValue.toString('html')
    : '');

class RichTextEditor extends Component {
  constructor(props) {
    super(props);

    const value = makeValue(props.defaultValue);

    this.state = {
      value,
      focused: false,
      onBlurTimeout: undefined,
      externallyChangedValue: this.props.externallyChangedValue
    };

    this.onChange = this.onChange.bind(this);
    this.onFocus = this.onFocus.bind(this);
    this.onBlur = this.onBlur.bind(this);

    this.dispatchChange = debounce((inputValue) =>
      props.onChange(inputValue), 300);
  }

  static getDerivedStateFromProps(props, currentState) {
    if (currentState.externallyChangedValue !== props.externallyChangedValue) {
      props.onChange(props.externallyChangedValue);
      return {
        externallyChangedValue: props.externallyChangedValue,
        value: makeValue(props.externallyChangedValue)
      };
    }
    return null;
  }

  componentWillUnmount() {
    const { onBlurTimeout } = this.state;
    clearTimeout(onBlurTimeout);
  }

  onChange(value) {
    const { onHeightChange } = this.props;
    const newContent = getEmptyStringOrHtmlContent(value);
    const prevContent = getEmptyStringOrHtmlContent(this.state.value);

    const didContentChange = newContent !== prevContent;
    const didLineCountChange = getLineCount(newContent) !== getLineCount(prevContent);

    this.setState({ value }, () => {
      // Keep focus/scroll at the bottom when new lines are added
      if (didLineCountChange && onHeightChange) {
        onHeightChange();
      }
    });

    if (didContentChange) {
      this.dispatchChange(newContent);
    }
  }

  onFocus() {
    this.setState({ focused: true }, () => {
      if (this.props.onHeightChange) {
        this.props.onHeightChange();
      }
    });
  }

  onBlur() {
    // We want to hide buttons on those RTE-editors that don't currently have active focus.
    //
    // When focus shifts from editor textarea to editor's text input for creating links, there is
    // a gap where the next target for focus, the text input, doesn't exist in the DOM. For the
    // blur event it appears as if focus goes away, to <body>. Even componentDidUpdate -event
    // cannot reliably tell if the future focus element has appeared: In Chrome it can be found,
    // in Firefox or Safari it cannot. Adding setTimeout(func, 0) doesn't help.
    //
    // The only reliable way seems to be to wait for a fixed amount of time, and then see if the
    // new focus element can be found from within the component.
    const { onBlurTimeout } = this.state;
    clearTimeout(onBlurTimeout);

    const timeout = setTimeout(() => {
      // eslint-disable-next-line react/no-find-dom-node
      if (!ReactDOM.findDOMNode(this).contains(document.activeElement)) {
        this.setState({ focused: false });
      }
    }, DELAY_BEFORE_HIDE);

    this.setState({ onBlurTimeout: timeout });
  }

  render() {
    const { maxLength, translate: t } = this.props;
    const inputLength = getEmptyStringOrHtmlContent(this.state.value).length;

    const toolbarConfig = {
      display: ['INLINE_STYLE_BUTTONS', 'BLOCK_TYPE_BUTTONS', 'LINK_BUTTONS'],
      INLINE_STYLE_BUTTONS: [
        { label: t('richText.bold'), style: 'BOLD' },
        { label: t('richText.italic'), style: 'ITALIC' },
        { label: t('richText.underline'), style: 'UNDERLINE' }
      ],
      BLOCK_TYPE_BUTTONS: [
        { label: t('richText.ul'), style: 'unordered-list-item' },
        { label: t('richText.ol'), style: 'ordered-list-item' }
      ]
    };
    const className = `${styles.container} ${this.state.focused && styles.focus} ${this.props.disabled && styles.disabled}`;
    return (
      <>
        <RTE
          value={this.state.value}
          onChange={this.onChange}
          toolbarConfig={toolbarConfig}
          placeholder={this.props.placeholder}
          onFocus={this.onFocus}
          onBlur={this.onBlur}
          editorClassName={this.props.isLarge ? styles.largeEditor : styles.editor}
          className={className}
          toolbarClassName={this.state.focused || !this.props.hideButtonsOnBlur ? '' : 'hidden'}
          readOnly={this.props.disabled}
        />
        {maxLength && inputLength > maxLength
        && <MaxLengthExceeded maxLength={maxLength} inputLength={inputLength} />}
      </>
    );
  }
}

RichTextEditor.propTypes = {
  onChange: func.isRequired,
  defaultValue: string.isRequired,
  externallyChangedValue: string,
  translate: func.isRequired,
  placeholder: string,
  hideButtonsOnBlur: bool,
  disabled: bool,
  maxLength: number,
  onHeightChange: func,
  isLarge: bool
};

RichTextEditor.defaultProps = {
  placeholder: '',
  hideButtonsOnBlur: false,
  disabled: false,
  externallyChangedValue: null,
  maxLength: null,
  onHeightChange: null,
  isLarge: false
};

export default withLang(RichTextEditor);
